import React, { useState, useEffect } from 'react';
import PocketBase from 'pocketbase';

const pb = new PocketBase('https://bahcarplaza.com');

function MobileCarDetails({ car, setView, goBack, cars = [], setSelectedCar }) {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showTestDriveModal, setShowTestDriveModal] = useState(false);
  const [testDriveData, setTestDriveData] = useState({ full_name: '', phone: '', booking_date: '', message: '' });

  // 🚨 Check if the car is sold 🚨
  const isSold = car?.status === 'Sold';

  // EMI Calculator State
  const carPrice = Number(car?.price) || 0;
  const [downPayment, setDownPayment] = useState(Math.round(carPrice * 0.2)); 
  const [loanDuration, setLoanDuration] = useState(60); 
  const interestRate = 9.5; 

  useEffect(() => {
    window.scrollTo(0, 0);
    const checkWishlist = async () => {
      if (pb.authStore.isValid && car?.id) {
        try {
          const res = await pb.collection('wishlist').getFullList({ filter: `user_id="${pb.authStore.model.id}" && car_id="${car.id}"` });
          if (res.length > 0) setIsWishlisted(true);
        } catch (e) { console.error(e); }
      }
    };
    checkWishlist();
    setDownPayment(Math.round((Number(car?.price) || 0) * 0.2));
    setCurrentImgIndex(0);
  }, [car]);

  if (!car) {
    return <div className="min-h-screen flex items-center justify-center bg-white font-bold text-gray-500">Loading Car Details...</div>;
  }

  const getVal = (val) => (Array.isArray(val) ? val[0] : val || "");
  
  // 🚨 FIXED: Added getImages function 🚨
  const getImages = (val) => Array.isArray(val) ? val : (val ? [val] : []);
  
  const images = getImages(car.image);

  const handleImageScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    const width = e.target.clientWidth;
    setCurrentImgIndex(Math.round(scrollLeft / width));
  };

  const handleAddToWishlist = async () => {
    if (!pb.authStore.isValid) return alert("Please log in to add to wishlist.");
    try {
      if (isWishlisted) {
        alert("Already in wishlist!"); 
      } else {
        await pb.collection('wishlist').create({ user_id: pb.authStore.model.id, car_id: car.id });
        setIsWishlisted(true);
        alert("Added to Wishlist! ❤️");
      }
    } catch (e) { alert("Error occurred."); }
  };

  const handleTestDriveSubmit = async (e) => {
    e.preventDefault();
    if (!pb.authStore.isValid) return alert("Please login first.");
    try {
      await pb.collection('bookings').create({ 
        ...testDriveData, 
        car_id: car.id,
        phone: testDriveData.phone.toString(), 
        booking_date: new Date(testDriveData.booking_date).toISOString(), 
        visit_type: 'Test Drive', 
        status: 'Pending', 
        user_id: pb.authStore.model.id 
      });
      alert("✅ Test Drive Requested Successfully!"); 
      setShowTestDriveModal(false);
    } catch (error) { alert("Error: " + error.message); }
  };

  const handleBookNow = () => {
    if (!pb.authStore.isValid) { alert("Please login to book this car."); setView('auth'); return; }
    setView('checkout_page'); 
  };

  // EMI Calculation Logic
  const loanAmount = carPrice - downPayment;
  const monthlyInterestRatio = (interestRate / 100) / 12;
  const top = Math.pow((1 + monthlyInterestRatio), loanDuration);
  const bottom = top - 1;
  const emi = loanAmount > 0 ? Math.round((loanAmount * monthlyInterestRatio * top) / bottom) : 0;

  // Similar Cars
  const similarCars = cars.filter(c => c.status === 'Approved' && c.id !== car.id && (c.brand === car.brand || c.body_type === car.body_type)).slice(0, 5);

  // Top Features List
  const featuresList = car.features ? (Array.isArray(car.features) ? car.features : car.features.split(',')) : [
    "Power Steering", "Airbags", "ABS", "Air Conditioner", "Power Windows", "Bluetooth", "Rear Parking Camera"
  ];

  return (
    <div className="min-h-screen bg-[#f4f7f6] font-sans pb-[90px]">
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          input[type=range] { -webkit-appearance: none; background: transparent; width: 100%; }
          input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; height: 20px; width: 20px; border-radius: 50%; background: #4b0082; cursor: pointer; margin-top: -7px; box-shadow: 0 2px 5px rgba(0,0,0,0.2); }
          input[type=range]::-webkit-slider-runnable-track { width: 100%; height: 6px; cursor: pointer; background: #d1d5db; border-radius: 4px; }
        `}
      </style>

      {/* 🚨 TOP NAVIGATION 🚨 */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center p-4 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
        <button onClick={goBack} className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white border-none cursor-pointer pointer-events-auto shadow-sm active:scale-95">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
        </button>
        <div className="flex gap-3 pointer-events-auto">
          <button onClick={() => setView('compare')} className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white border-none cursor-pointer shadow-sm active:scale-95">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
          </button>
          <button onClick={handleAddToWishlist} className={`w-10 h-10 backdrop-blur-md rounded-full flex items-center justify-center border-none cursor-pointer shadow-sm active:scale-95 ${isWishlisted ? 'bg-white text-red-500' : 'bg-white/20 text-white'}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill={isWishlisted ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          </button>
        </div>
      </div>

      {/* 🚨 CAR IMAGE SLIDER 🚨 */}
      <div className={`relative w-full h-[320px] bg-gray-100 ${isSold ? 'grayscale-[40%]' : ''}`}>
        <div className="w-full h-full flex overflow-x-auto snap-x snap-mandatory hide-scrollbar" onScroll={handleImageScroll}>
          {images.length > 0 ? images.map((img, i) => (
            <img key={i} src={`https://bahcarplaza.com/api/files/${car.collectionId}/${car.id}/${img}`} className="w-full h-full object-cover shrink-0 snap-center" alt="car" />
          )) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 font-bold">No Image Available</div>
          )}
        </div>
        
        {/* 🚨 SOLD OUT BADGE OVERLAY 🚨 */}
        {isSold && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20 pointer-events-none">
            <span className="bg-red-600 text-white font-black px-6 py-2 rounded-xl border-4 border-white shadow-2xl transform -rotate-12 text-3xl uppercase tracking-widest">Sold Out</span>
          </div>
        )}

        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-md">
            {images.map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${currentImgIndex === i ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`}></div>
            ))}
          </div>
        )}
      </div>

      {/* 🚨 TITLE & PRICE 🚨 */}
      <div className="bg-white p-5 rounded-b-[24px] shadow-sm mb-2">
        <h1 className="m-0 text-xl font-black text-[#1b1d28] uppercase leading-tight">{car.year} {car.brand} {car.model}</h1>
        <div className="mt-4 border-b border-gray-100 pb-4 flex justify-between items-end">
          <div>
            <h2 className={`m-0 font-black text-2xl ${isSold ? 'text-gray-400 line-through decoration-red-500' : 'text-[#1b1d28]'}`}>₹{(car.price || 0).toLocaleString()}</h2>
            <p className="m-0 text-gray-400 font-bold text-xs mt-1">Ex-showroom <span className="text-[#4b0082] font-black underline decoration-dashed">{getVal(car.city) || 'City'}</span></p>
          </div>
        </div>
      </div>

      {/* 🚨 KEY SPECIFICATIONS 🚨 */}
      <div className="p-5 bg-white mb-2 shadow-sm rounded-[24px]">
        <h3 className="text-[16px] font-black text-gray-500 mb-4">Key Specifications</h3>
        <div className="grid grid-cols-3 gap-3">
          
          <div className="bg-[#f8f9fa] p-3 rounded-xl flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-gray-50">
            <span className="text-[22px] mb-1 opacity-70">🕹️</span>
            <span className="text-[10px] font-bold text-gray-400">Transmission</span>
            <span className="text-[12px] font-black text-[#1b1d28] mt-0.5">{getVal(car.transmission) || 'Manual'}</span>
          </div>

          <div className="bg-[#f8f9fa] p-3 rounded-xl flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-gray-50">
            <span className="text-[22px] mb-1 opacity-70">⛽</span>
            <span className="text-[10px] font-bold text-gray-400">Fuel Type</span>
            <span className="text-[12px] font-black text-[#1b1d28] mt-0.5">{getVal(car.fuel_type) || 'Petrol'}</span>
          </div>

          <div className="bg-[#f8f9fa] p-3 rounded-xl flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-gray-50">
            <span className="text-[22px] mb-1 opacity-70">⚙️</span>
            <span className="text-[10px] font-bold text-gray-400">Engine</span>
            <span className="text-[12px] font-black text-[#1b1d28] mt-0.5">{car.engine_cc || '999cc'}</span>
          </div>

          <div className="bg-[#f8f9fa] p-3 rounded-xl flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-gray-50">
            <span className="text-[22px] mb-1 opacity-70">💺</span>
            <span className="text-[10px] font-bold text-gray-400">Seating</span>
            <span className="text-[12px] font-black text-[#1b1d28] mt-0.5">{getVal(car.seats) || '5 Seater'}</span>
          </div>

          <div className="bg-[#f8f9fa] p-3 rounded-xl flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-gray-50">
            <span className="text-[22px] mb-1 opacity-70">🛡️</span>
            <span className="text-[10px] font-bold text-gray-400">Airbags</span>
            <span className="text-[12px] font-black text-[#1b1d28] mt-0.5">Yes</span>
          </div>

          <div className="bg-[#f8f9fa] p-3 rounded-xl flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-gray-50">
            <span className="text-[22px] mb-1 opacity-70">🛣️</span>
            <span className="text-[10px] font-bold text-gray-400">Mileage</span>
            <span className="text-[12px] font-black text-[#1b1d28] mt-0.5">{car.mileage || 'NA'} kmpl</span>
          </div>

        </div>
      </div>

      {/* 🚨 BASIC INFO 🚨 */}
      <div className="p-6 bg-white mb-2 shadow-sm rounded-[24px]">
        <h3 className="text-[18px] font-black text-[#4b0082] mb-6">Basic info</h3>
        <div className="grid grid-cols-2 gap-y-6 gap-x-4">
          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">Make Year</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{car.year}</p></div>
          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">Reg. Year</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{car.year}</p></div>
          
          <div className="col-span-2 h-[1px] bg-gray-100"></div>

          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">Fuel</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{getVal(car.fuel_type)}</p></div>
          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">Km driven</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{(car.kilometers_driven || 0).toLocaleString()} km</p></div>
          
          <div className="col-span-2 h-[1px] bg-gray-100"></div>

          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">Transmission</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{getVal(car.transmission)}</p></div>
          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">No. of Owner(s)</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{getVal(car.owner_type) || '1st Owner'}</p></div>
          
          <div className="col-span-2 h-[1px] bg-gray-100"></div>

          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">RTO</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{car.number_plate ? car.number_plate.substring(0,4) : 'NA'}</p></div>
          <div><p className="m-0 text-[11px] font-bold text-gray-400 mb-1">Car Location</p><p className="m-0 text-[14px] font-black text-[#1b1d28]">{getVal(car.city)}</p></div>
        </div>
      </div>

      {/* 🚨 TOP FEATURES 🚨 */}
      <div className="p-6 bg-white mb-2 shadow-sm rounded-[24px]">
        <h3 className="text-[18px] font-black text-[#4b0082] mb-5">Top Features</h3>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Comfort & Convenience</p>
        <div className="flex flex-col gap-4">
          {featuresList.map((feature, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-gray-300 font-bold">✓</span>
              <span className="text-[14px] font-black text-[#1b1d28] flex-1">{feature.trim()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 🚨 EMI CALCULATOR 🚨 */}
      <div className="p-6 bg-white mb-2 shadow-sm rounded-[24px]">
        <h3 className="text-[18px] font-black text-[#4b0082] mb-6">EMI calculator</h3>
        
        <div className="mb-6">
          <div className="flex justify-between items-end mb-3">
            <span className="text-[13px] font-black text-[#1b1d28]">Loan Amount</span>
            <span className="text-[16px] font-black text-[#4b0082]">₹{loanAmount > 0 ? loanAmount.toLocaleString() : 0}</span>
          </div>
          <input type="range" min="0" max={carPrice} value={loanAmount > 0 ? loanAmount : 0} onChange={(e) => setDownPayment(carPrice - Number(e.target.value))} />
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-end mb-3">
            <span className="text-[13px] font-black text-[#1b1d28]">Down Payment</span>
            <span className="text-[16px] font-black text-[#4b0082]">₹{downPayment.toLocaleString()}</span>
          </div>
          <input type="range" min="0" max={carPrice} value={downPayment} onChange={(e) => setDownPayment(Number(e.target.value))} />
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-end mb-3">
            <span className="text-[13px] font-black text-[#1b1d28]">Duration of Loan</span>
            <span className="text-[16px] font-black text-[#4b0082]">{loanDuration} Months</span>
          </div>
          <input type="range" min="12" max="84" step="6" value={loanDuration} onChange={(e) => setLoanDuration(Number(e.target.value))} />
          <div className="flex justify-between mt-2 text-[10px] text-gray-400 font-bold"><span>12 Months</span><span>84 Months</span></div>
        </div>

        <div className="mt-8 text-center">
          <h2 className="m-0 text-[32px] font-black text-[#4b0082]">₹{emi.toLocaleString()} <span className="text-[12px] font-bold text-gray-500">per month</span></h2>
          <p className="text-[10px] text-gray-400 mt-4 font-bold px-4 leading-relaxed">Disclaimer: Applicable rate of interest can vary subject to credit profile. Loan approval is at the sole discretion of the financer.</p>
        </div>
      </div>

      {/* 🚨 SIMILAR CARS 🚨 */}
      {similarCars.length > 0 && (
        <div className="p-5 bg-white mb-4 shadow-sm rounded-[24px]">
          <h3 className="text-[18px] font-black text-[#1b1d28] mb-4">Similar Cars</h3>
          <div className="flex overflow-x-auto gap-4 hide-scrollbar snap-x">
            {similarCars.map(sCar => {
              const sImg = getImages(sCar.image)[0];
              return (
                <div key={sCar.id} onClick={() => { setSelectedCar(sCar); window.scrollTo(0,0); }} className="shrink-0 w-[200px] border border-gray-100 rounded-2xl overflow-hidden snap-start cursor-pointer shadow-sm pb-2">
                  <img src={sImg ? `https://bahcarplaza.com/api/files/${sCar.collectionId}/${sCar.id}/${sImg}` : ''} className="w-full h-[120px] object-cover bg-gray-100" alt="car"/>
                  <div className="p-3">
                    <h4 className="m-0 text-[#1b1d28] font-black text-[13px] truncate">{sCar.brand} {sCar.model}</h4>
                    <p className="m-0 text-gray-500 text-[10px] font-bold mt-1">{(sCar.kilometers_driven || 0).toLocaleString()} km • {getVal(sCar.fuel_type)}</p>
                    <h4 className="m-0 text-[#4b0082] font-black text-[14px] mt-2">₹{(sCar.price || 0).toLocaleString()}</h4>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 🚨 TEST DRIVE MODAL 🚨 */}
      {showTestDriveModal && (
        <div className="fixed inset-0 bg-[#1b1d28]/80 z-[9999] flex items-end sm:items-center justify-center backdrop-blur-sm">
          <div className="bg-white w-full sm:max-w-[400px] rounded-t-[24px] sm:rounded-[24px] p-6 shadow-2xl relative">
             <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="font-black text-xl text-[#e72b58] m-0 uppercase">Book Test Drive</h2>
                  <p className="text-gray-500 font-bold text-[11px] m-0 mt-1">For {car.brand} {car.model}</p>
                </div>
                <button onClick={() => setShowTestDriveModal(false)} className="bg-gray-100 border-none w-8 h-8 rounded-full font-bold cursor-pointer text-gray-500">✕</button>
             </div>
             <form onSubmit={handleTestDriveSubmit} className="flex flex-col gap-4">
                <input required type="text" placeholder="Full Name" onChange={(e) => setTestDriveData({...testDriveData, full_name: e.target.value})} className="p-4 rounded-xl border border-gray-200 bg-[#f8f9fa] font-bold text-[13px] outline-none" />
                <input required type="number" placeholder="Phone Number" onChange={(e) => setTestDriveData({...testDriveData, phone: e.target.value})} className="p-4 rounded-xl border border-gray-200 bg-[#f8f9fa] font-bold text-[13px] outline-none" />
                <input required type="datetime-local" onChange={(e) => setTestDriveData({...testDriveData, booking_date: e.target.value})} className="p-4 rounded-xl border border-gray-200 bg-[#f8f9fa] font-bold text-[13px] outline-none" />
                <textarea placeholder="Any specific message? (Optional)" onChange={(e) => setTestDriveData({...testDriveData, message: e.target.value})} className="p-4 rounded-xl border border-gray-200 bg-[#f8f9fa] font-bold text-[13px] outline-none h-[80px] resize-none" />
                <button type="submit" className="w-full py-4 mt-2 rounded-xl bg-[#e72b58] text-white font-black text-[14px] uppercase tracking-wide shadow-lg border-none active:scale-95">Confirm Booking ✓</button>
             </form>
          </div>
        </div>
      )}

      {/* 🚨 STICKY BOTTOM ACTION BAR (Dynamic depending on Sold status) 🚨 */}
      <div className="fixed bottom-0 left-0 right-0 p-3 bg-white flex gap-3 shadow-[0_-5px_20px_rgba(0,0,0,0.08)] z-40 border-t border-gray-100">
        {isSold ? (
          <button disabled className="w-full bg-gray-200 text-gray-400 rounded-xl py-4 flex flex-col items-center justify-center border-none cursor-not-allowed">
            <span className="font-black text-[14px] tracking-wide uppercase">🚫 OUT OF STOCK</span>
          </button>
        ) : (
          <>
            <button onClick={handleBookNow} className="flex-1 bg-[#4b0082] text-white rounded-xl py-3 flex flex-col items-center justify-center border-none shadow-md active:scale-95 transition-transform cursor-pointer">
              <span className="font-black text-[14px] tracking-wide uppercase">Book Now</span>
              <span className="text-[9px] opacity-80 mt-0.5 font-bold tracking-wider">100% refundable</span>
            </button>
            <button onClick={() => setShowTestDriveModal(true)} className="flex-1 bg-[#e72b58] text-white rounded-xl py-3 flex flex-col items-center justify-center border-none shadow-md active:scale-95 transition-transform cursor-pointer">
              <span className="font-black text-[14px] tracking-wide uppercase">Free Test Drive</span>
            </button>
          </>
        )}
      </div>

    </div>
  );
}

export default MobileCarDetails;